import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Tab, Tabs } from '@material-ui/core';

import { MediaDetailUtils } from '../utils';
import { setInspectingMediaData } from '../../../../actions/inspectScreenActions';
import { getFormRegistrations, updateForm } from '../../../../actions/publishActions';
import { mediaTypes } from '../../../../constants/mediaConstants';
import { showNotification } from '../../../../actions/notificationActions';
import ConfirmationDialog from '../../../../reusable/ConfirmationDialog/ConfirmationDialog';
import DetailsSection from '../../../FormsAndRegistration/form/DetailsSection';
import ConversionsSection from '../../../FormsAndRegistration/form/ConversionsSection';
import SettingSection from '../../../FormsAndRegistration/form/SettingSection';

type FormContentProps = {
	baseClassName: string;
	formDetail: any;
	formMediaId: string;
	handleOnDataChanges: () => void;
	handleOnDataChangesCallback: () => void;
};

const FormContent = forwardRef(
	(
		{ baseClassName, formDetail, formMediaId, handleOnDataChanges, handleOnDataChangesCallback }: FormContentProps,
		ref
	) => {
		const { t } = useTranslation();
		const dispatch = useDispatch<any>();
		const { defaultAccountId } = useSelector((state: any) => state.session);

		const [selectedTab, setSelectedTab] = useState<string>('conversions');
		const [registrations, setRegistrations] = useState<any[]>([]);
		const [hasMoreRegistrations, setHasMoreRegistrations] = useState<boolean>(false);
		const [regOffset, setRegOffset] = useState<number>(0);
		const [updatedFormTemplate, setUpdatedFormTemplate] = useState<any | null>(null);
		const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);

		useImperativeHandle(ref, () => {
			return { onSaveChanges };
		});

		const onSaveChanges = () => {
			setShowConfirmationDialog(true);
		};

		const handleSaveChanges = (choice: { ok: boolean }) => {
			if (choice.ok) {
				try {
					if (!updatedFormTemplate || !formDetail.id) {
						return;
					}
					const id = formDetail.id;
					dispatch(updateForm(defaultAccountId, id, updatedFormTemplate)).then((respData: any) => {
						if (respData && respData.id) {
							setUpdatedFormTemplate(null);
							dispatch(showNotification(t('INSPECT_MEDIA_MINI_FORM_PAGE_SUCCESS_MSG')));
							// update the formDetails
							dispatch(setInspectingMediaData({ id, formMediaId, type: mediaTypes.form }));
							handleOnDataChangesCallback();
						}
					});
				} catch (e) {
					console.error('Error updating form template', e);
				}
			}
			setShowConfirmationDialog(false);
		};

		const formUpdateHandler = (data: any) => {
			setUpdatedFormTemplate({ ...formDetail, ...data });
			handleOnDataChanges();
		};

		const getRegistrations = (searchKey: string = '') => {
			const formId = formDetail.id;
			setRegOffset(0);
			dispatch(getFormRegistrations(defaultAccountId, formId, formMediaId, searchKey, 0)).then((respData: any) => {
				if (respData && Array.isArray(respData)) {
					setRegistrations(respData);
					setHasMoreRegistrations(respData.length === 50);
				}
			});
		};

		const loadMoreRegistrations = (searchKey: string = ''): Promise<any[]> => {
			const formId = formDetail.id;
			const nextOffset = regOffset + 50;
			return dispatch(getFormRegistrations(defaultAccountId, formId, formMediaId, searchKey, nextOffset))
				.then((respData: any) => {
					if (respData && Array.isArray(respData)) {
						setHasMoreRegistrations(respData.length === 50);
						setRegOffset(nextOffset);
						return respData;
					}
					return [];
				})
				.catch(() => []);
		};

		useEffect(() => {
			getRegistrations();
		}, [formDetail, formMediaId, defaultAccountId]);

		useEffect(() => {
			if (selectedTab !== 'conversions') {
				setRegOffset(0);
				setHasMoreRegistrations(registrations.length === 50);
			}
		}, [selectedTab]);

		return (
			<div className={`${baseClassName}__media-content-container`} style={{ overflow: 'auto' }}>
				<Tabs
					value={selectedTab}
					onChange={(_e, value) => setSelectedTab(value)}
					orientation="vertical"
					className={`${baseClassName}__tab-bar`}
					TabIndicatorProps={{
						style: {
							color: '#ffffff',
							backgroundColor: 'transparent',
						},
					}}
				>
					{MediaDetailUtils.MenuFormItems.map((item) => (
						<Tab
							key={item.value}
							value={item.value}
							icon={selectedTab === item.value ? item.activeIcon : item.icon}
							style={
								selectedTab === item.value
									? { backgroundColor: '#126CFC1A', opacity: '1' }
									: { opacity: '1' }
							}
						/>
					))}
				</Tabs>

				<div className={`${baseClassName}__media-content-container`}>
					<div className="inspect-media__detail-container">
						<div className={'inspect-media__general-info-container'}>
							{selectedTab === 'detail' && (
								<DetailsSection
									inspectorTheme={true}
									formTemplate={formDetail}
									onChange={formUpdateHandler}
								/>
							)}

							{selectedTab === 'conversions' && (
								<ConversionsSection
									inspectorTheme={true}
									formTemplate={formDetail}
									registrations={registrations}
									isLoading={false}
									hasMore={hasMoreRegistrations}
									onSearch={getRegistrations}
									onChange={getRegistrations}
									onLoadMore={loadMoreRegistrations}
								/>
							)}
							{selectedTab === 'setting' && (
								<SettingSection
									inspectorTheme={true}
									formTemplate={formDetail}
									onChange={formUpdateHandler}
								/>
							)}
						</div>
					</div>
				</div>

				{showConfirmationDialog && (
					<ConfirmationDialog
						title={t('COMMON_SAVE_CHANGES')}
						callback={handleSaveChanges}
						message={t('INSPECT_MEDIA_MINI_FORM_PAGE_CONFIRM_MSG')}
						okButtonText={t('COMMON_YES')}
						cancelButtonText={t('COMMON_CANCEL')}
					/>
				)}
			</div>
		);
	}
);

export default FormContent;
